import { useCallback, useContext } from 'react';

import { TOAST_STATUS } from '@evinced-private/ui-common';

import { NotificationsContext } from 'src/common/providers/notificationsProvider/NotificationsContext';
import {
	getInstructionStatus,
	IInstructionStatusResponse
} from 'src/common/services/api/InstructionService';
import { Logger } from 'src/common/services/Logger';

import { DeleteProduct } from '../services/api/DeleteRunService';

import { DeleteEntityName } from './useDeleteRun.constants';
import {
	INSTRUCTION_STATUS,
	MAX_POLL_ATTEMPTS,
	POLL_INTERVAL_MS,
	SUCCESS_MESSAGE_BY_ENTITY
} from './useInstructionPolling.constants';
import { INSTRUCTION_POLLING_LABELS } from './useInstructionPolling.labels';

const delay = (ms: number): Promise<void> =>
	new Promise((resolve) => {
		setTimeout(resolve, ms);
	});

export const useInstructionPolling = (
	product: DeleteProduct,
	entityName: DeleteEntityName,
	onDone: () => void,
	onSettled?: () => void
): { startPolling: (instructionIds: string[]) => void } => {
	const { toast } = useContext(NotificationsContext);

	const pollSingle = useCallback(
		async (instructionId: string, attempt = 0): Promise<IInstructionStatusResponse> => {
			if (attempt >= MAX_POLL_ATTEMPTS) {
				Logger.error('useInstructionPolling: max poll attempts reached', {
					product,
					instructionId,
					attempt
				});
				throw new Error(`Polling timed out for instruction ${instructionId}`);
			}

			const data = await getInstructionStatus(product, instructionId);

			if (data.status === INSTRUCTION_STATUS.IN_PROGRESS) {
				await delay(POLL_INTERVAL_MS);
				return pollSingle(instructionId, attempt + 1);
			}

			return data;
		},
		[product]
	);

	const startPolling = useCallback(
		(instructionIds: string[]): void => {
			Promise.all(instructionIds.map((id) => pollSingle(id)))
				.then((results) => {
					const allDone = results.every((r) => r.status === INSTRUCTION_STATUS.DONE);
					if (allDone) {
						onDone();
						toast({
							announcement: SUCCESS_MESSAGE_BY_ENTITY[entityName],
							status: TOAST_STATUS.SUCCESS,
							show: true
						});
					} else {
						toast({
							announcement: INSTRUCTION_POLLING_LABELS.FAILURE,
							status: TOAST_STATUS.FAIL,
							show: true
						});
					}
					onSettled?.();
				})
				.catch((err) => {
					Logger.error('useInstructionPolling: polling aborted', { product, instructionIds, err });
					toast({
						announcement: INSTRUCTION_POLLING_LABELS.FAILURE,
						status: TOAST_STATUS.FAIL,
						show: true
					});
					onSettled?.();
				});
		},
		[entityName, onDone, onSettled, pollSingle, product, toast]
	);

	return { startPolling };
};
